/**
 * Wallet-backed Anchor program builder for KotH (Last-Touch) writes.
 *
 * The explorer's `client/src/explorer/anchor.ts` builds a read-only program with a
 * stub wallet — sufficient for `program.account.kothRound.fetch(...)` polling. For
 * the touch / init / end ix calls we need the user's connected wallet as signer, so
 * this module wraps wallet-adapter's `WalletContextState` into Anchor's `Wallet`
 * shape.
 */
import {
  AnchorProvider,
  BN,
  Program,
  type Idl as AnchorIdl,
} from "@coral-xyz/anchor";
import type { WalletContextState } from "@solana/wallet-adapter-react";
import {
  Connection,
  PublicKey,
  type Transaction,
  type VersionedTransaction,
} from "@solana/web3.js";
import { IDL, PROGRAM_ID } from "@solblox/shared";

/**
 * Adapter from wallet-adapter-react's `WalletContextState` to Anchor's `Wallet`
 * interface. AnchorProvider needs `{ publicKey, signTransaction, signAllTransactions }`;
 * wallet-adapter exposes the same methods but only when the wallet is connected, so
 * `buildWalletProgram` will throw if `wallet.publicKey` is null.
 */
class WalletAdapterWallet {
  constructor(private readonly wallet: WalletContextState) {
    if (!wallet.publicKey) throw new Error("wallet not connected");
    if (!wallet.signTransaction || !wallet.signAllTransactions) {
      throw new Error("connected wallet does not support signing");
    }
  }
  get publicKey(): PublicKey {
    return this.wallet.publicKey as PublicKey;
  }
  async signTransaction<T extends Transaction | VersionedTransaction>(
    tx: T,
  ): Promise<T> {
    return (await this.wallet.signTransaction!(tx)) as T;
  }
  async signAllTransactions<T extends Transaction | VersionedTransaction>(
    txs: T[],
  ): Promise<T[]> {
    return (await this.wallet.signAllTransactions!(txs)) as T[];
  }
}

/** Build a write-capable Anchor `Program` for LastTouch. Throws if disconnected. */
export function buildWalletProgram(
  connection: Connection,
  wallet: WalletContextState,
): Program {
  const provider = new AnchorProvider(
    connection,
    new WalletAdapterWallet(wallet),
    { commitment: "confirmed", preflightCommitment: "confirmed" },
  );
  return new Program(IDL as unknown as AnchorIdl, provider);
}

/** Re-export so call-sites don't reach into shared. */
export function getProgramId(): PublicKey {
  return new PublicKey(PROGRAM_ID);
}

/**
 * Derive the round PDA at `[b"koth_round", round_id.to_le_bytes()]`. Mirrors the
 * impl in `tools/koth-play.mjs` and `program/tests/koth.spec.ts`.
 */
export function deriveRoundPda(roundId: BN, programId?: PublicKey): PublicKey {
  const buf = Buffer.alloc(8);
  buf.writeBigUInt64LE(BigInt(roundId.toString()));
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("koth_round"), buf],
    programId ?? getProgramId(),
  );
  return pda;
}

/** Derive the season PDA at `[b"season", season_id]`. */
export function deriveSeasonPda(
  seasonId: Uint8Array,
  programId?: PublicKey,
): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("season"), Buffer.from(seasonId)],
    programId ?? getProgramId(),
  );
  return pda;
}

/**
 * Derive the per-(round, wallet) LastTouch PDA at
 * `[b"last_touch", round_pda, wallet]`. Returned account holds the wallet's
 * cooldown timestamp + per-round touch count (spec §8 #1–#2).
 */
export function deriveLastTouchPda(
  roundPda: PublicKey,
  wallet: PublicKey,
  programId?: PublicKey,
): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("last_touch"), roundPda.toBuffer(), wallet.toBuffer()],
    programId ?? getProgramId(),
  );
  return pda;
}

/**
 * Derive the per-(season, wallet) LeaderboardEntry PDA at
 * `[b"leaderboard", season_pda, wallet]`. Holds a wallet's accumulated
 * unclaimed lamports across rounds in a season; credited at `end_round`,
 * drained at `claim_round_payout`.
 */
export function deriveLeaderboardEntryPda(
  seasonPda: PublicKey,
  wallet: PublicKey,
  programId?: PublicKey,
): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("leaderboard"), seasonPda.toBuffer(), wallet.toBuffer()],
    programId ?? getProgramId(),
  );
  return pda;
}
